/**
 * Quem foi mencionado numa mensagem de chat.
 *
 * O autocomplete do ChatInput insere o NOME DE EXIBIÇÃO (`@Maria Silva`), que
 * costuma ter espaço e acento. A detecção anterior era `/@(\w+)/g` comparada com
 * a parte local do e-mail: `\w+` não atravessa espaço e quase nenhum nome de
 * exibição é igual ao apelido do e-mail, então a menção feita pelo autocomplete
 * nunca era reconhecida — e o mencionado não recebia aviso nenhum.
 */

export interface UsuarioMencionavel {
  nome?: string | null;
  email?: string | null;
}

/** Sem acento, sem caixa e com espaços colapsados — como o texto é comparado. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** O caractere seguinte encerra a menção? Letra ou dígito significa outro nome. */
function terminaAMencao(caractere: string | undefined): boolean {
  return caractere === undefined || !/[\p{L}\p{N}]/u.test(caractere);
}

function contemMencaoA(conteudoNormalizado: string, apelidoNormalizado: string): boolean {
  if (!apelidoNormalizado) return false;

  const alvo = `@${apelidoNormalizado}`;
  let de = conteudoNormalizado.indexOf(alvo);

  while (de !== -1) {
    // O @ tem que abrir a menção: no começo do texto ou depois de um separador.
    // Sem isso, `maria.silva@empresa.com` contaria como menção ao apelido.
    const anterior = de === 0 ? undefined : conteudoNormalizado[de - 1];
    const abreMencao = de === 0 || !/[\p{L}\p{N}.@_-]/u.test(anterior as string);
    // E o nome tem que terminar ali: `@Maria Silvana` não é `@Maria Silva`.
    const seguinte = conteudoNormalizado[de + alvo.length];

    if (abreMencao && terminaAMencao(seguinte)) return true;

    de = conteudoNormalizado.indexOf(alvo, de + 1);
  }

  return false;
}

export function mensagemMencionaUsuario(
  conteudo: string | null | undefined,
  usuario: UsuarioMencionavel
): boolean {
  if (!conteudo) return false;

  const normalizado = normalizar(conteudo);
  const apelidoDoEmail = usuario.email?.split('@')[0];

  return [usuario.nome, apelidoDoEmail]
    .filter((valor): valor is string => !!valor && valor.trim().length > 0)
    .some(valor => contemMencaoA(normalizado, normalizar(valor)));
}
